import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointer,
  Users,
  Heart,
  Share2,
  MessageCircle,
  DollarSign,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
  Activity,
  Clock,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter } from
'lucide-react';
import ConsistencyHeatmap from '../components/ConsistencyHeatmap';

// Mock data for analytics
const overviewMetrics = {
  impressions: { value: 1250000, change: 12.5, trend: 'up' },
  reach: { value: 890000, change: 8.3, trend: 'up' },
  engagement: { value: 45600, change: -2.1, trend: 'down' },
  clicks: { value: 23400, change: 15.7, trend: 'up' },
  followers: { value: 24700, change: 5.2, trend: 'up' },
  posts: { value: 156, change: 23.4, trend: 'up' }
};

const channelPerformance = [
{ channel: 'instagram', name: 'Instagram', impressions: 450000, engagement: 18500, followers: 12500, growth: 8.2 },
{ channel: 'facebook', name: 'Facebook', impressions: 380000, engagement: 12300, followers: 8900, growth: 4.5 },
{ channel: 'linkedin', name: 'LinkedIn', impressions: 220000, engagement: 8900, followers: 3200, growth: 12.1 },
{ channel: 'youtube', name: 'YouTube', impressions: 150000, engagement: 4500, followers: 2100, growth: 6.8 },
{ channel: 'x', name: 'X (Twitter)', impressions: 50000, engagement: 1400, followers: 1500, growth: -1.2 }];


const topContent = [
{ id: 1, title: 'Product Launch Video', platform: 'instagram', engagement: 5600, reach: 45000 },
{ id: 2, title: 'Behind the Scenes', platform: 'facebook', engagement: 3200, reach: 32000 },
{ id: 3, title: 'How-To Tutorial', platform: 'youtube', engagement: 2800, reach: 28000 },
{ id: 4, title: 'Team Spotlight', platform: 'linkedin', engagement: 2100, reach: 18000 },
{ id: 5, title: 'Flash Sale Announcement', platform: 'instagram', engagement: 1800, reach: 22000 }];


const platformIcons = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  x: Twitter
};

const platformColors = {
  instagram: 'from-pink-500 to-purple-600',
  facebook: 'from-blue-500 to-blue-700',
  linkedin: 'from-sky-500 to-blue-600',
  youtube: 'from-red-500 to-red-700',
  x: 'from-gray-700 to-gray-900'
};

export const Analytics = () => {const { t } = useTranslation("cns");
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedWorkspace, setSelectedWorkspace] = useState(1);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="space-y-6" data-testid="analytics">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl">
              <TrendingUp className="h-7 w-7 text-white" />
            </div>{t("analytics", "Analytics")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackPerformanceAcross", "Track performance across all your social channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Tabs value={timeRange} onValueChange={setTimeRange}>
            <TabsList>
              <TabsTrigger value="7d">{t("7d", "7D")}</TabsTrigger>
              <TabsTrigger value="30d">{t("30d", "30D")}</TabsTrigger>
              <TabsTrigger value="90d">{t("90d", "90D")}</TabsTrigger>
              <TabsTrigger value="1y">{t("1y", "1Y")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />{t("custom", "Custom")}

          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {Object.entries(overviewMetrics).map(([key, metric]) =>
        <Card key={key} className="card-elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground capitalize">{key}</span>
                <Badge
                variant="outline"
                className={metric.trend === 'up' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                
                  {metric.trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {Math.abs(metric.change)}%
                </Badge>
              </div>
              <p className="text-2xl font-bold">{formatNumber(metric.value)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Consistency Heatmap */}
        <div className="lg:col-span-2">
          <ConsistencyHeatmap workspaceId={selectedWorkspace} />
        </div>

        {/* Best Performing Content */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-600" />{t("topContent", "Top Content")}

            </CardTitle>
            <CardDescription>{t("bestPerformingPosts", "Best performing posts this period")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topContent.map((content, index) => {
                const Icon = platformIcons[content.platform] || Share2;
                return (
                  <div
                    key={content.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-sm font-medium">
                      {index + 1}
                    </div>
                    <div className={`p-1.5 rounded-lg bg-gradient-to-br ${platformColors[content.platform]} text-white`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{content.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatNumber(content.engagement)}{t("engagements", "engagements \u2022")}{formatNumber(content.reach)}{t("reach", "reach")}
                      </p>
                    </div>
                  </div>);

              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Channel Performance */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" />{t("channelPerformance", "Channel Performance")}

          </CardTitle>
          <CardDescription>{t("breakdownBySocial", "Breakdown by social media platform")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("channel", "Channel")}</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t("impressions", "Impressions")}</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t("engagement", "Engagement")}</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t("followers", "Followers")}</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t("growth", "Growth")}</th>
                </tr>
              </thead>
              <tbody>
                {channelPerformance.map((channel) => {
                  const Icon = platformIcons[channel.channel] || Share2;
                  return (
                    <tr key={channel.channel} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${platformColors[channel.channel]} text-white`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className="font-medium">{channel.name}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-medium">{formatNumber(channel.impressions)}</td>
                      <td className="text-right py-3 px-4 font-medium">{formatNumber(channel.engagement)}</td>
                      <td className="text-right py-3 px-4 font-medium">{formatNumber(channel.followers)}</td>
                      <td className="text-right py-3 px-4">
                        <Badge
                          variant="outline"
                          className={channel.growth >= 0 ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                          
                          {channel.growth >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                          {Math.abs(channel.growth)}%
                        </Badge>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Best Time to Post */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />{t("bestTimesTo", "Best Times to Post")}

          </CardTitle>
          <CardDescription>{t("optimalPostingTimes", "Optimal posting times based on your audience engagement")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {[
            { day: 'Monday', time: '10:00 AM', score: 92 },
            { day: 'Tuesday', time: '2:00 PM', score: 88 },
            { day: 'Wednesday', time: '11:00 AM', score: 95 },
            { day: 'Thursday', time: '3:00 PM', score: 85 },
            { day: 'Friday', time: '12:00 PM', score: 90 }].
            map((slot) =>
            <div
              key={slot.day}
              className="p-4 rounded-lg bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100">
              
                <p className="font-medium text-indigo-700">{slot.day}</p>
                <p className="text-2xl font-bold text-indigo-900">{slot.time}</p>
                <div className="flex items-center gap-1 mt-2">
                  <div className="flex-1 h-2 rounded-full bg-indigo-200">
                    <div
                    className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                    style={{ width: `${slot.score}%` }} />
                  
                  </div>
                  <span className="text-xs text-indigo-600 font-medium">{slot.score}%</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default Analytics;